import { Container, DefaultPageLayout } from 'routes/components/Layout'
import { LINKS } from 'routes/constants'
import { BreadcrumbsNavigation } from 'routes/components/Shared'
import { BestSupport } from 'routes/components/Features/BestSupport'
import {
  getSolutionsBreadcrumbs,
  PageType,
  SolutionsHeroType,
} from 'routes/Solutions/helpers'
import { SolutionHeadMetatag } from 'routes/Solutions/components/SolutionHeadMetatag'
import { SolutionHero } from 'routes/Solutions/components/SolutionHero'
import { SolutionHIL } from 'routes/Solutions/components/SolutionHIL'
import { SolutionHowItWorks } from 'routes/Solutions/components/SolutionHowItWorks'
import { SolutionCompanies } from 'routes/Solutions/components/SolutionCompanies'
import { SolutionInfrastructure } from 'routes/Solutions/components/SolutionInfrastructure'
import oneClickIntegrationsMob1x from './one-click-integrations-mob@1x.avif'
import oneClickIntegrationsMob2x from './one-click-integrations-mob@2x.avif'
import oneClickIntegrationsDesk1x from './one-click-integrations-desk@1x.avif'
import oneClickIntegrationsDesk2x from './one-click-integrations-desk@2x.avif'
import importData1x from './import-data@1x.avif'
import importData2x from './import-data@2x.avif'
import authentication1x from './authentication@1x.webp'
import authentication2x from './authentication@2x.webp'
import integrationWorksExample1x from './integration-works-example@1x.avif'
import integrationWorksExample2x from './integration-works-example@2x.avif'
import customizeIntegration1x from './customize-integration@1x.avif'
import customizeIntegration2x from './customize-integration@2x.avif'
import classes from '../solutions.module.scss'

export const pageData: PageType = {
  title: 'One-Click Integrations',
  href: LINKS.SOLUTIONS.ONE_CLICK_INTEGRATIONS,
  imageSrc: `${oneClickIntegrationsMob1x.src}`,
  srcSet: `${oneClickIntegrationsMob1x.src} 1x, ${oneClickIntegrationsMob2x.src} 2x`,
  description:
    'Let your customers enable integrations in one click - and then configure if they want to. Build integration blueprints that work across multiple customers and multiple applications, add customization options, and deploy the integrations at scale.',
}

const pageHeroData: SolutionsHeroType = {
  title: 'One-click Integrations',
  subtitle:
    'Let your customers enable integrations in one click - and then configure if they want to.',
  imageWidth: '352',
  imageHeight: '225',
  mobile1x: oneClickIntegrationsMob1x,
  mobile2x: oneClickIntegrationsMob2x,
  desktop1x: oneClickIntegrationsDesk1x,
  desktop2x: oneClickIntegrationsDesk2x,
  showAsRow: true,
}

export default function OneClickIntegrations() {
  const breadcrumbs = getSolutionsBreadcrumbs(pageData.title)

  return (
    <DefaultPageLayout className={classes.page}>
      <SolutionHeadMetatag pageData={pageData} />
      <Container>
        <BreadcrumbsNavigation
          breadcrumbs={breadcrumbs}
          className={classes.breadcrumbs}
        />
      </Container>
      <Container grid>
        <SolutionHero {...pageHeroData} />
        <SolutionHIL
          titles={[
            { text: 'Select another SaaS to import data from' },
            { text: 'Go through the authentication flow' },
            { text: "That's it. Integration just works", h64: true },
            {
              text: 'Configure and customize integration using options defined by developer (you)',
            },
          ]}
        >
          <img
            srcSet={`${importData1x.src} 1x, ${importData2x.src} 2x`}
            src={importData1x.src}
            alt='Select another SaaS to import data from'
            width={568}
            height={302}
            loading='lazy'
          />
          <img
            srcSet={`${authentication1x.src} 1x, ${authentication2x.src} 2x`}
            src={authentication1x.src}
            alt='Go through the authentication flow'
            width={568}
            height={302}
            loading='lazy'
          />
          <img
            srcSet={`${integrationWorksExample1x.src} 1x, ${integrationWorksExample2x.src} 2x`}
            src={integrationWorksExample1x.src}
            alt='Integration works'
            width={382}
            height={72}
            loading='lazy'
          />
          <img
            srcSet={`${customizeIntegration1x.src} 1x, ${customizeIntegration2x.src} 2x`}
            src={customizeIntegration1x.src}
            alt='Configure and customize integration'
            width={566}
            height={300}
            loading='lazy'
          />
        </SolutionHIL>
        <SolutionHowItWorks
          steps={[
            'Select apps you want your customers to integrate',
            'Build integration use cases',
            'Create UI to connect to external apps on behalf of your customers',
            'Optionally, add UI to configure enabled integrations',
          ]}
        />
        <SolutionCompanies />
        <SolutionInfrastructure />
        <BestSupport
          isSolutionsSection
          subtitle={
            <>
              Integrations are hard and messy. <br /> Our success team will help
              you every step of the way.
            </>
          }
          className={classes.support}
        />
      </Container>
    </DefaultPageLayout>
  )
}
